import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { keycloak } from './keycloak';
import { useKeycloakAuth } from './useKeycloakAuth';

/**
 * Route guard for Keycloak-authenticated pages.
 *
 * Behavior:
 *  - If `keycloak.authenticated` is true, render `children`.
 *  - If the user is not authenticated, render an inline sign-in card
 *    with a "Continue with Google" button. The user MUST click to
 *    proceed — we do NOT auto-redirect to Keycloak, because that
 *    previously caused:
 *      (a) every page mount of a guarded route while logged out to
 *          bounce to Google's account picker, and
 *      (b) the /work-order loop the user reported, where re-renders
 *          re-evaluated this guard and re-fired keycloak.login().
 *    The user is in control of when to authenticate.
 *
 *  - Once they click, the existing LoginPage flow (or direct
 *    keycloak.login) handles the redirect and the subsequent callback
 *    brings them back to the originally requested page (via
 *    `redirectUri: window.location.href`).
 *
 * Sourcing auth state from the Keycloak singleton (not Redux) is
 * deliberate: the access token lives only in Keycloak memory and is
 * never persisted to localStorage. A hard-reload of `/work-order` must
 * read `keycloak.authenticated` so an existing SSO session still works.
 */
type AuthGuardProps = {
    children: React.ReactElement;
};

const SignInPrompt: React.FC<{ returnTo: string }> = ({ returnTo }) => {
    const { login } = useKeycloakAuth();
    return (
        <div className="flex w-screen h-screen items-center justify-center bg-surface-low">
            <div className="shadow-e-03 max-md:max-w-[478px] max-md:w-auto w-[478px] p-[32px] flex flex-col gap-[24px] rounded-[12px] overflow-hidden box-border bg-white">
                <div className="flex justify-center">
                    <h1 className="text-heading-5 text-high-em font-semibold">
                        Welcome Auto Track
                    </h1>
                </div>
                <p className="text-body text-mid-em text-center">
                    Sign in with your work email to continue.
                </p>
                <div className="flex flex-col w-full gap-[16px] pt-[8px]">
                    <button
                        type="button"
                        className="ant-btn ant-btn-block ant-btn-lg"
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 10,
                            height: 48,
                            padding: '0 16px',
                            backgroundColor: '#ffffff',
                            border: '1px solid #dadce0',
                            borderRadius: 8,
                            color: '#3c4043',
                            fontFamily:
                                'Google Sans, Roboto, system-ui, -apple-system, Segoe UI, sans-serif',
                            fontSize: 14,
                            fontWeight: 500,
                            cursor: 'pointer',
                        }}
                        onClick={() => login('google', returnTo)}
                    >
                        Continue with Google
                    </button>
                </div>
            </div>
        </div>
    );
};

export const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
    const navigate = useNavigate();
    const [returnTo] = React.useState(() => window.location.href);

    useEffect(() => {
        // If we somehow end up here while already authenticated (e.g.
        // hot-reload after a Keycloak session became valid), bounce
        // to the requested route. We don't render children until
        // `keycloak.authenticated` is true below, so this is just a
        // safety net for the race where Keycloak init finishes after
        // the guard has already mounted.
        if (keycloak.authenticated) {
            // No-op: render branch below will pick up `authenticated`
            // and render `children`.
        }
    }, [navigate]);

    if (keycloak.authenticated) {
        return children;
    }

    return <SignInPrompt returnTo={returnTo} />;
};
